import { useState, useEffect, useRef } from "react";
import { RotateCcw, Lock, Unlock } from "lucide-react";
import { cn } from "../../lib/cn.js";

/**
 * Slug input that auto-derives from a source field (typically `name` or
 * `title`) UNTIL the user manually edits it. The lock indicator makes it
 * obvious that auto-derivation has been disabled.
 *
 *   <SlugField
 *     value={form.watch("slug")}
 *     onChange={(v) => form.setValue("slug", v)}
 *     source={form.watch("name")}
 *   />
 */
function slugify(input) {
  return String(input ?? "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

export function SlugField({ value, onChange, source, label = "Slug", hint, error }) {
  const [auto, setAuto] = useState(!value); // auto-derive while empty
  const lastSourceRef = useRef(source);

  useEffect(() => {
    if (auto && source !== lastSourceRef.current) {
      lastSourceRef.current = source;
      onChange(slugify(source));
    }
  }, [source, auto, onChange]);

  const handleChange = (e) => {
    setAuto(false);
    onChange(slugify(e.target.value));
  };

  const handleReset = () => {
    setAuto(true);
    onChange(slugify(source));
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="block text-xs font-medium uppercase tracking-wider text-text-secondary">
          {label}
        </label>
        <div className="flex items-center gap-1 text-xs">
          {auto ? (
            <span className="inline-flex items-center gap-1 text-text-muted">
              <Unlock className="h-3 w-3" />
              auto from name
            </span>
          ) : (
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-accent hover:text-accent-hover"
            >
              <RotateCcw className="h-3 w-3" />
              reset to auto
            </button>
          )}
        </div>
      </div>
      <div className="relative">
        <input
          type="text"
          value={value ?? ""}
          onChange={handleChange}
          className={cn(
            "w-full rounded-md border bg-bg-elevated py-2 pl-8 pr-3 text-sm text-text-primary placeholder:text-text-muted",
            "focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent",
            error ? "border-danger focus:border-danger focus:ring-danger" : "border-border"
          )}
          placeholder="auto-generated from name"
        />
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
          {auto ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
        </span>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
      {!error && hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}
